/**
 * Get platform version using User-Agent Client Hints API
 * This is the ONLY way to accurately distinguish Windows 10 from Windows 11
 * @returns {Promise<string|null>} Platform version (e.g., "13.0.0" for Windows 11, "10.0.0" for Windows 10)
 */
export async function getPlatformVersion() {
  try {
    console.log('[Platform Detection] Checking for User-Agent Client Hints API...');
    console.log('[Platform Detection] navigator.userAgentData available:', !!navigator.userAgentData);
    
    // Check if User-Agent Client Hints API is available
    if (navigator.userAgentData && navigator.userAgentData.getHighEntropyValues) {
      console.log('[Platform Detection] Requesting high entropy values...');
      const uaData = await navigator.userAgentData.getHighEntropyValues(['platformVersion']);
      console.log('[Platform Detection] Platform version received:', uaData.platformVersion);
      
      if (uaData.platformVersion) {
        return uaData.platformVersion;
      }
    }
    
    console.log('[Platform Detection] User-Agent Client Hints API not available, using fallback detection');
    
    // FALLBACK: Use user agent string to make educated guess
    // This won't be 100% accurate but better than nothing
    const ua = navigator.userAgent;
    
    if (ua.includes('Windows NT 10.0')) {
      // Try to detect Windows 11 vs 10 using other indicators
      // Windows 11 typically has Chrome 96+ or Edge 96+
      const chromeMatch = ua.match(/Chrome\/(\d+)/);
      const edgeMatch = ua.match(/Edg\/(\d+)/);
      
      if (chromeMatch) {
        const version = parseInt(chromeMatch[1]);
        // Chrome 96+ was released alongside Windows 11
        // This is a heuristic but better than showing "10/11"
        if (version >= 110) {
          // Likely Windows 11 (Chrome 110+ is from 2023+)
          console.log('[Platform Detection] Fallback: Detected likely Windows 11 based on Chrome version', version);
          return '13.0.0'; // Windows 11 platform version
        } else if (version >= 96) {
          // Could be either, default to 11 for newer browsers
          console.log('[Platform Detection] Fallback: Detected Windows 10/11 based on Chrome version', version);
          return '13.0.0'; // Assume Windows 11 for Chrome 96+
        } else {
          // Older Chrome, likely Windows 10
          console.log('[Platform Detection] Fallback: Detected likely Windows 10 based on Chrome version', version);
          return '10.0.0'; // Windows 10 platform version
        }
      } else if (edgeMatch) {
        const version = parseInt(edgeMatch[1]);
        if (version >= 110) {
          return '13.0.0'; // Likely Windows 11
        } else if (version >= 96) {
          return '13.0.0'; // Assume Windows 11
        } else {
          return '10.0.0'; // Windows 10
        }
      }
      
      // No Chrome/Edge version found, default to Windows 11 for modern browsers
      console.log('[Platform Detection] Fallback: Defaulting to Windows 11 for modern browser');
      return '13.0.0';
    }
    
    return null;
  } catch (error) {
    console.error('[Platform Detection] Error getting platform version:', error);
    return null;
  }
}

/**
 * Get comprehensive platform information
 * @returns {Promise<Object>} Platform information including version
 */
export async function getPlatformInfo() {
  try {
    if (navigator.userAgentData && navigator.userAgentData.getHighEntropyValues) {
      const uaData = await navigator.userAgentData.getHighEntropyValues([
        'platform',
        'platformVersion',
        'architecture',
        'model',
        'uaFullVersion'
      ]);
      
      return {
        platform: uaData.platform || null,
        platformVersion: uaData.platformVersion || null,
        architecture: uaData.architecture || null,
        model: uaData.model || null,
        uaFullVersion: uaData.uaFullVersion || null
      };
    }
    return {
      platform: null,
      platformVersion: null,
      architecture: null,
      model: null,
      uaFullVersion: null
    };
  } catch (error) {
    console.error('[Platform Detection] Error getting platform info:', error);
    return {
      platform: null,
      platformVersion: null,
      architecture: null,
      model: null,
      uaFullVersion: null
    };
  }
}
